import { combine, createEffect, createEvent, createStore, sample } from 'effector/compat'
import { ICatalogGoodBase } from '@entities/goods/contract'
import * as catalogApi from '@api/catalog'
import { IShipment } from 'src/shared/contract/shipment'
import { sumPrice, updateBasketPostitions, updateBasketPostitionsAndChangePrice } from '@helpers/order'
import { IGoodBasket } from '@entities/basket/contract'
import { $shipment } from '@entities/shipment/model'
import { useStoreMap } from 'effector-react/compat'
import { subscribeLoggerEffect } from 'src/infastruct/effector-utils/subscribe-sentry'
import { parseCookies } from 'nookies'
import { $currentPath } from '@entities/route/model'
import { geThresholdFx } from 'src/features/order/model/threshold'
import { persist, StorageAdapter } from 'src/shared/lib/effector-storage'
import { delay } from 'patronum'

const KEY_CUSTOMER_STORAGE = 'zdorovru-customer'

interface IChangeBasketItemCount {
    goodId: number
    count: number
}

export const $basket = createStore([] as IGoodBasket[], {
    serialize: 'ignore'
})

export const adapter: StorageAdapter = () => {
    return {
        get() {
            if (typeof window === 'undefined') return null
            if (!localStorage) return null
            const storageCookie = localStorage.getItem(KEY_CUSTOMER_STORAGE)
            if (!storageCookie) return []
            const customerJson = JSON.parse(storageCookie)
            return customerJson?.customer?.basketItems || []
        },
        set(value) {
            if (typeof window === 'undefined') return null
            if (!localStorage) return null
            const storageCookie = localStorage.getItem(KEY_CUSTOMER_STORAGE)
            if (!storageCookie) {
                localStorage.setItem(KEY_CUSTOMER_STORAGE, JSON.stringify({ customer: { basketItems: value } }))
                return
            }
            const customerJson = JSON.parse(storageCookie)
            if (!customerJson.customer) {
                localStorage.setItem(KEY_CUSTOMER_STORAGE, JSON.stringify({ customer: { basketItems: value } }))
                return
            }
            localStorage.setItem(
                KEY_CUSTOMER_STORAGE,
                JSON.stringify({
                    ...customerJson,
                    customer: {
                        ...customerJson.customer,
                        basketItems: value
                    }
                })
            )
        }
    }
}

export const pickupBasketUpdate = createEvent()
export const pickupBasket = createEvent()

export const donePickup = createEvent()

persist({
    key: KEY_CUSTOMER_STORAGE,
    store: $basket,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    adapter,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    done: donePickup,
    pickup: pickupBasketUpdate
})
export const clearBasketByOrderId = createEvent<number>()
export const stopClearBasket = createEvent()
const $clearBasketOrderId = createStore(0).reset(stopClearBasket)
const clearBasket = createEvent<number>()

sample({
    clock: clearBasketByOrderId,
    target: $clearBasketOrderId
})

sample({
    clock: clearBasketByOrderId,
    filter: (orderId) => !!orderId,
    target: clearBasket
})

delay({
    source: clearBasketByOrderId,
    timeout: 2000,
    target: clearBasket
})
export const addGoodBasket = createEvent<ICatalogGoodBase>()
export const deleteBasketItem = createEvent<number>()
export const decrementBasketItemCount = createEvent<number>()
export const incrementBasketItemCount = createEvent<number>()
export const changeBasketItemCount = createEvent<IChangeBasketItemCount>()
export const deleteConfirmBasketItem = createEvent<number>()
export const hiddenBasketItem = createEvent<number>()
export const visibleBasketItem = createEvent<number>()
// confirm delete
export const $deleteConfirmationGoodId = createStore<number>(0)
export const applyConfirmationDelete = createEvent()
export const cancelConfirmationDelete = createEvent()

sample({
    clock: applyConfirmationDelete,
    source: $deleteConfirmationGoodId,
    target: deleteBasketItem
})

sample({
    clock: deleteBasketItem,
    source: $basket,
    fn: (basketItems, goodId) => {
        const res = [...basketItems.filter((x) => x.webData.goodId !== goodId)]
        return res
    },
    target: [$basket, cancelConfirmationDelete]
})

sample({
    clock: deleteConfirmBasketItem,
    target: $deleteConfirmationGoodId
})

sample({
    clock: cancelConfirmationDelete,
    fn: () => 0,
    target: $deleteConfirmationGoodId
})

sample({
    clock: hiddenBasketItem,
    source: $basket,
    fn: (basketItems, goodId) => {
        return basketItems.map((x: IGoodBasket): IGoodBasket => {
            if (x.webData.goodId !== goodId) return x
            return { ...x, isHidden: true }
        })
    },
    target: $basket
})

sample({
    clock: visibleBasketItem,
    source: $basket,
    fn: (basketItems, goodId) => {
        return basketItems.map((x: IGoodBasket): IGoodBasket => {
            if (x.webData.goodId !== goodId) return x
            return { ...x, isHidden: false }
        })
    },
    target: $basket
})

sample({
    clock: decrementBasketItemCount,
    source: $basket,
    filter: (basketItems, goodId) => (basketItems?.find((x) => x.webData.goodId === goodId)?.count || 0) <= 1,
    fn: (_, goodId) => goodId,
    target: $deleteConfirmationGoodId
})

enum ChangeType {
    decrement,
    increment,
    custom
}

const updatePricePosition = (item: IGoodBasket) => {
    item.deliveryPriceSum = sumPrice(
        item.deliveryPrice?.normal || 0,
        item.count,
        item.isAvailable,
        item.deliveryStatus?.isAvailableForDelivery || false
    )
    item.stockPriceSum = sumPrice(item.stockPrice?.normal || 0, item.count, item.isAvailable, item.isAvailableOnStock)
    item.minPriceSum = sumPrice(item.minPrice?.normal || 0, item.count, true, true)
    return item
}

const changeCountGoodFromBasketItems = (
    basketItems: IGoodBasket[],
    goodId: number,
    type: ChangeType,
    customCount?: number
) => {
    return basketItems.map((item) => {
        if (item.webData.goodId !== goodId) return item
        switch (type) {
            case ChangeType.custom:
                if (!customCount || customCount < 1) return item
                item.count = customCount
                if (item.isChanged && item.bookedCount && item.bookedCount >= item.count) item.isChanged = false
                item = updatePricePosition(item)
                return { ...item }
            case ChangeType.decrement:
                item.count = item.count - 1
                if (item.isChanged && item.bookedCount && item.bookedCount >= item.count) item.isChanged = false
                item = updatePricePosition(item)
                return { ...item }
            case ChangeType.increment:
                item.count = item.count + 1
                if (item.isChanged && item.bookedCount && item.bookedCount >= item.count) item.isChanged = false
                item = updatePricePosition(item)
                return { ...item }
        }
    })
}

sample({
    clock: decrementBasketItemCount,
    source: $basket,
    filter: (basketItems, goodId) => (basketItems?.find((x) => x.webData.goodId === goodId)?.count || 0) > 1,
    fn: (basketItems, goodId) => changeCountGoodFromBasketItems(basketItems, goodId, ChangeType.decrement),
    target: $basket
})

sample({
    clock: incrementBasketItemCount,
    source: $basket,
    //filter: (basketItems, goodId) => (basketItems?.find((x) => x.webData.goodId === goodId)?.count || 0) > 1,
    fn: (basketItems, goodId) => changeCountGoodFromBasketItems(basketItems, goodId, ChangeType.increment),
    target: $basket
})

sample({
    clock: changeBasketItemCount,
    source: $basket,
    fn: (basketItems, { goodId, count }) =>
        changeCountGoodFromBasketItems(basketItems, goodId, ChangeType.custom, count),
    target: $basket
})

export const $basketActualPositions = combine($basket, (items) => items?.filter((x) => !x.isHidden))

const convertGoodToBasketGood = (good: ICatalogGoodBase, count = 1): IGoodBasket =>
    ({
        ...good,
        count,
        deliveryPriceSum: sumPrice(
            good.deliveryPrice?.normal || 0,
            count,
            good.isAvailable,
            good.deliveryStatus?.isAvailableForDelivery || false
        ),
        stockPriceSum: sumPrice(good.stockPrice?.normal || 0, count, good.isAvailable, good.isAvailableOnStock),
        minPriceSum: sumPrice(good.minPrice?.normal || 0, count, true, true),
        isHidden: false
    } as IGoodBasket)

sample({
    clock: addGoodBasket,
    source: $basket,
    filter: (basketItems: IGoodBasket[], good: ICatalogGoodBase) =>
        !basketItems.some((x) => x.webData.goodId === good.webData.goodId),
    fn: (basketItems: IGoodBasket[], good: ICatalogGoodBase) => [...basketItems, convertGoodToBasketGood(good)],
    target: $basket
})

export const updateBasketFx = createEffect<
    {
        basketItems: IGoodBasket[]
        shipment: IShipment
    },
    {
        basketItems: IGoodBasket[]
    },
    Error
>({
    handler: async ({ basketItems, shipment }) => {
        if (!basketItems || basketItems.length <= 0) return { basketItems: [] }
        const response = (await catalogApi.getUpdatedGoods(
            basketItems.map((item) => ({
                webId: item.webData.webId,
                goodId: item.webData.goodId
            })),
            shipment
        )) as {
            basketItems: IGoodBasket[]
        }
        const updatedBasketItems = updateBasketPostitions(basketItems, response.basketItems)
        return { basketItems: updatedBasketItems }
    }
})
export const updateBasketWithPriceFx = createEffect<
    {
        basketItems: IGoodBasket[]
        shipment: IShipment
    },
    {
        basketItems: IGoodBasket[]
    },
    Error
>({
    handler: async ({ basketItems, shipment }) => {
        if (!basketItems || basketItems.length <= 0) return { basketItems: [] }
        const response = (await catalogApi.getUpdatedGoods(
            basketItems.map((item) => ({
                webId: item.webData.webId,
                goodId: item.webData.goodId
            })),
            shipment
        )) as {
            basketItems: IGoodBasket[]
        }
        const updatedBasketItems = updateBasketPostitionsAndChangePrice(basketItems, response.basketItems)
        return { basketItems: updatedBasketItems }
    }
})
subscribeLoggerEffect(updateBasketWithPriceFx)

sample({
    clock: updateBasketFx.doneData,
    fn: (clock) => clock.basketItems,
    target: $basket
})

sample({
    clock: pickupBasket,
    source: {
        basketItems: $basket,
        shipment: $shipment
    },
    filter: ({ basketItems }) => basketItems.length > 0,
    fn: ({ basketItems, shipment }) => ({
        basketItems,
        shipment
    }),
    target: updateBasketFx
})

sample({
    clock: $currentPath,
    filter: (path) => path.includes('/basket'),
    target: pickupBasket
})

sample({
    clock: $shipment,
    source: $basket,
    fn: (basketItems, shipment) =>
        ({
            basketItems,
            shipment
        } as {
            basketItems: IGoodBasket[]
            shipment: IShipment
        }),
    target: updateBasketWithPriceFx
})

sample({
    clock: updateBasketWithPriceFx.doneData,
    fn: ({ basketItems }) => basketItems,
    target: $basket
})

sample({
    clock: donePickup,
    source: $clearBasketOrderId,
    filter: (orderId) => !!orderId,
    target: clearBasket
})

sample({
    clock: clearBasket,
    source: $basket,
    filter: (basketItems, orderId) => {
        if (!basketItems || basketItems.length === 0) return false
        const cookies = parseCookies()
        const cookieOrderId = cookies.orderid
        if (cookieOrderId) {
            const cookieOrderIdInt = parseInt(cookieOrderId)
            if (cookieOrderIdInt === orderId) {
                return true
            }
        }
        return false
    },
    fn: (basketItems): IGoodBasket[] => {
        const hiddenItems = basketItems.filter((x) => x.isHidden)
        if (!hiddenItems || hiddenItems.length === 0) return []
        return hiddenItems.map((x) => ({ ...x, isHidden: false }))
    },
    target: [$basket, stopClearBasket]
})
/**
 * init threshold event
 */
sample({
    clock: $basket,
    filter: (items) => items?.length > 0,
    fn: (basketItems) => ({
        basketItems
    }),
    target: geThresholdFx
})
export const useBasketCount = () => useStoreMap($basket, (items) => items?.length || 0)
export const useBasketGoodPositionCount = (goodId: number) =>
    useStoreMap({
        store: $basket,
        keys: [goodId],
        fn: (basketItems, [id]) => basketItems?.find((x) => x.webData.goodId === id)?.count || 0
    })
